/**
 * The "points at" link between a written note and the thing it labels (#157).
 *
 * A note with a callout IS a relationship between two objects, so it gets the
 * same affordances every other relationship in this app has: a chip on each
 * side's row in the parts list, hovering one lights up the other, clicking
 * jumps to the far side, and a Connections row on the note's own panel with a
 * × that unpins it. Before this, the link was a sentence — "Points at Front
 * panel." — that you could read but not follow.
 *
 * The far side is a part, one of its cutouts, or one of its seams (#1076), and
 * the chip lands on THAT row rather than on the part's: a note about a seam is
 * about the seam, and putting its chip on the part would make the maker guess
 * which of five seams was meant.
 *
 * Deliberately NOT registered with the `CONNECTIONS` registry. That registry
 * drives construction-LINE chips and the commit-pipeline derive / prune passes;
 * a note relates a note to a PART and has neither, so registering would add an
 * entry that answers "nothing" to every question the registry asks. What is
 * shared — and what matters — is the chip / row spec shape, so the panels
 * render these through exactly the same code path as the rest.
 */
import type { Note, Piece, Project } from "../Project";
import {
  noteAnchorTarget,
  noteAnchors,
  noteTargetKey,
  noteTargetLabel,
  withNoteAnchors,
  type NoteTarget,
} from "../notes";
import type { ConnectionChipSpec, ConnectionRowSpec } from "./Connection";
import noteChipRaw from "../../icons/chip-note.svg?raw";
import { stripIconSvg } from "../../icons/stripIconSvg";

const KIND = "note" as const;

/** A card with a leader running back to the thing it labels (currentColor;
 *  CSS sizes it to the chip width). */
export const NOTE_CHIP_ICON_SVG = stripIconSvg(noteChipRaw);

/**
 * One key per LINK — a note and one thing it points at — shared by both ends:
 * the chip on the note's row and the chip on the target's row. Hovering either
 * lights up the other. A note pointing at three things has three keys, so
 * hovering one highlights one pair rather than the whole fan.
 */
export function noteConnectionKey(noteId: string, target: NoteTarget): string {
  return `note:${noteId}:${noteTargetKey(target)}`;
}

function partLabel(project: Project, piece: Piece): string {
  return piece.name || `Part ${project.pieces.indexOf(piece) + 1}`;
}

/** One thing a note points at, resolved against the project: the part it lives
 *  on, plus a name to read and a selection to jump to. */
export interface NoteTargetRef {
  target: NoteTarget;
  piece: Piece;
  /** How the target reads in a chip or a Connections row — the same names the
   *  parts list shows on the rows themselves, so the two agree. */
  label: string;
}

/**
 * Everything a note points at, in callout order, skipping any target that has
 * since been deleted — a note worth keeping, but a link not worth drawing.
 *
 * A cutout or seam whose PART survives but which is itself gone drops out here
 * even though `resolveNoteAnchor` still draws its leader against the part
 * outline: the leader is geometry with a sensible fallback, while a chip is a
 * claim about a specific object, and there is no longer an object to click
 * through to.
 */
export function notedTargets(project: Project, note: Note): NoteTargetRef[] {
  const out: NoteTargetRef[] = [];
  for (const a of noteAnchors(note)) {
    const piece = project.pieces.find((p) => p.id === a.pieceId);
    if (!piece) continue;
    const target = noteAnchorTarget(a);
    if (target.cutoutId || target.stitchId) {
      // Same names the parts list and the printed page use, so one object is
      // never called two things.
      const on = noteTargetLabel(piece, target);
      // Gone: the leader still draws against the part outline, but there is no
      // longer an object for a chip to lead to.
      if (!on) continue;
      out.push({
        target,
        piece,
        label: `${on} on ${partLabel(project, piece)}`,
      });
      continue;
    }
    out.push({ target, piece, label: partLabel(project, piece) });
  }
  return out;
}

/** Notes pointing at one part or anything on it, in list order (so their
 *  numbers read in order). */
export function notesForPiece(project: Project, pieceId: string): Note[] {
  return (project.notes ?? []).filter((n) =>
    noteAnchors(n).some((a) => a.pieceId === pieceId),
  );
}

/** Chips on a NOTE's row — one per thing it points at. Clicking one selects
 *  that thing. */
export function noteChipSpecs(
  project: Project,
  note: Note,
): ConnectionChipSpec[] {
  return notedTargets(project, note).map((ref) => ({
    kind: KIND,
    // Keyed by BOTH ends, so hovering the chip for one target lights up that
    // target's chip and not every one this note happens to touch.
    connectionKey: noteConnectionKey(note.id, ref.target),
    role: "points-at",
    iconSvg: NOTE_CHIP_ICON_SVG,
    ariaLabel: `Points at ${ref.label}`,
    title: `Points at ${ref.label}`,
    className: "note-chip",
    // Canvas hover refs describe construction geometry only, and leather is
    // not construction geometry — so the canvas stays quiet and the
    // cross-highlight is panel-side, driven by the shared connection key.
    hoverRefs: [],
    clickTarget: {
      pieceId: ref.target.pieceId,
      ...(ref.target.cutoutId ? { cutoutId: ref.target.cutoutId } : {}),
      ...(ref.target.stitchId ? { stitchId: ref.target.stitchId } : {}),
    },
  }));
}

/**
 * Chips on a target's row — one per note pointing at it, so a part with three
 * notes shows three chips and each one leads to its own note. One combined
 * chip would collapse exactly the information the maker wants: WHICH note.
 *
 * Matches on the FULL target, so a note on a seam puts its chip on the seam's
 * row and nowhere else.
 */
export function targetNoteChipSpecs(
  project: Project,
  target: NoteTarget,
): ConnectionChipSpec[] {
  const key = noteTargetKey(target);
  return (project.notes ?? [])
    .map((note, i) => ({ note, number: i + 1 }))
    .filter(({ note }) =>
      noteAnchors(note).some((a) => noteTargetKey(noteAnchorTarget(a)) === key),
    )
    .map(({ note, number }) => ({
      kind: KIND,
      connectionKey: noteConnectionKey(note.id, target),
      role: "noted-by",
      iconSvg: NOTE_CHIP_ICON_SVG,
      ariaLabel: `Note ${number} points here`,
      title: `Note ${number} points here`,
      className: "note-chip",
      hoverRefs: [],
      clickTarget: { noteId: note.id },
    }));
}

/** Chips on a PART's row — notes pointing at the part ITSELF. Notes about one
 *  of its cutouts or seams are chipped on those rows instead. */
export function partNoteChipSpecs(
  project: Project,
  pieceId: string,
): ConnectionChipSpec[] {
  return targetNoteChipSpecs(project, { pieceId });
}

/** Drops ONE link, keeping the note and its other callouts. Omit `target` to
 *  drop them all. The inverse of dragging a tip onto bare canvas. */
export function unpinNote(noteId: string, target?: NoteTarget) {
  const key = target ? noteTargetKey(target) : null;
  return (project: Project): Project => ({
    ...project,
    notes: (project.notes ?? []).map((n) =>
      n.id === noteId
        ? withNoteAnchors(
            n,
            key == null
              ? []
              : noteAnchors(n).filter(
                  (a) => noteTargetKey(noteAnchorTarget(a)) !== key,
                ),
          )
        : n,
    ),
  });
}

/** The Connections rows on a note's own panel — one per thing it points at,
 *  each with the × that unpins that one. */
export function noteRowSpecs(
  project: Project,
  note: Note,
): ConnectionRowSpec[] {
  const refs = notedTargets(project, note);
  return noteChipSpecs(project, note).map((chip, i) => ({
    ...chip,
    label: chip.title,
    onDetach: (editor) =>
      editor.commit("Unpin note", unpinNote(note.id, refs[i]!.target)),
  }));
}
